import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import classNames from 'classnames';
import { useAppraisalTabsConfig } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/context/appraisal-tabs-context';

const TAB_CONTROL_BASE_CLASS_NAME =
  'appraise-by-tab-control appraise-by-tab-control-v2 size-16 pt-0 text-gray-darker text-decoration-none pb-0_5';
const TAB_LABEL_BASE_CLASS_NAME = 'appraise-by-tab-label text-capitalize fw-normal';

/**
 * This is a pure-config consumer — it only read stable flags. Switched from useAppraisalTabsContext() to useAppraisalTabsConfig(). They will no longer re-render on form state changes.
 */
function AppraiseByTabsHeaderItemBase({
  tabId,
  label,
  onBtnClick,
  isActive = false,
  isFirstItem = false,
  isLastItem = false,
  isAppraisalValuePage = false,
}) {
  const { isBackgroundStyleTabEnabled, renderAddressTab } = useAppraisalTabsConfig();
  const handleClick = useCallback(() => {
    onBtnClick(tabId);
  }, [onBtnClick, tabId]);

  let tabControlPaddingClassName;
  let tabControlBackgroundStyleClassName;

  if (isAppraisalValuePage) {
    tabControlPaddingClassName = isFirstItem ? 'ps-0 pe-0_5' : 'px-0_5';
  } else if (isBackgroundStyleTabEnabled) {
    tabControlPaddingClassName = classNames('pt-0_75', renderAddressTab ? 'px-0_75 px-sm-1_5 px-md-2' : 'px-1', {
      'pb-0': isActive,
    });
    tabControlBackgroundStyleClassName = classNames('m-0', {
      'rounded-top-4': isFirstItem || isLastItem,
      'rounded-end-0': isFirstItem && !isLastItem,
      'rounded-start-0': isLastItem && !isFirstItem,
    });
  } else {
    tabControlPaddingClassName = isFirstItem ? 'ps-0 pe-0_5 pe-md-0_75' : 'px-0_5 px-md-0_75';
  }

  const tabControlStateClassName = classNames({
    active: isActive,
    'text-primary-darker': isActive && isBackgroundStyleTabEnabled,
    'bg-gray-lightest': !isActive && isBackgroundStyleTabEnabled,
    'border-0': !isBackgroundStyleTabEnabled,
  });

  const tabControlClassName = classNames(
    TAB_CONTROL_BASE_CLASS_NAME,
    `tab-${tabId.toLowerCase()}`,
    tabControlStateClassName,
    tabControlPaddingClassName,
    tabControlBackgroundStyleClassName
  );

  const tabLabelClassName = classNames(TAB_LABEL_BASE_CLASS_NAME, {
    'pb-0_5': isBackgroundStyleTabEnabled,
  });

  return (
    <Button
      role="tab"
      color={isBackgroundStyleTabEnabled ? 'custom' : undefined}
      className={tabControlClassName}
      onClick={handleClick}
      data-testid={`tab-${tabId}`}
      data-tracking-id="appraisal_tab_selected"
      data-tracking-value={label}
      data-no-refresh
      aria-selected={isActive}
    >
      <span className={tabLabelClassName}>{label}</span>
    </Button>
  );
}

AppraiseByTabsHeaderItemBase.propTypes = {
  tabId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onBtnClick: PropTypes.func.isRequired,
  isActive: PropTypes.bool,
  isFirstItem: PropTypes.bool,
  isLastItem: PropTypes.bool,
  isAppraisalValuePage: PropTypes.bool,
};

export const AppraiseByTabsHeaderItem = memo(AppraiseByTabsHeaderItemBase);
